import React, { useEffect, useState } from 'react'

import PropTypes from 'prop-types'
import { useParams, Link } from 'react-router-dom'
import { connect } from 'react-redux'
import {
  IonButton,
  IonIcon,
  IonText,
  IonAccordion,
  IonAccordionGroup,
  IonItem,
  IonLabel,
} from '@ionic/react'
import { printOutline } from 'ionicons/icons'
import { LoadingModal, Modal, SafeHtml } from 'components'
import infoImg from 'images/info.svg'
import { StatusTile } from '../../workOrders/components'
import VisitContent from '../components/VisitContent'
import SpecialActionsForm from '../forms/SpecialActionsForm'
import PartsOrderPoNumberForm from '../forms/PartsOrderPoNumberForm'
import { selectors } from '../../workOrders/reducer'
import * as actions from 'workOrders-actions'
import * as apiActions from 'api-actions'
import { ORDER_STATUS, workOrder as workOrderType } from 'types'
import { formatUTCAsLocalDate, formatUTCAsLocalDateTime } from 'utils'
import { looksLikeHtml } from 'utils/sanitizeHtml'

const propTypes = {
  acceptWorkOrder: PropTypes.func.isRequired,
  addTechCommentsWorkOrder: PropTypes.func.isRequired,
  clearWorkOrder: PropTypes.func.isRequired,
  declineWorkOrder: PropTypes.func.isRequired,
  fetchManagerWorkOrder: PropTypes.func.isRequired,
  inProgressChangeStatusWorkOrder: PropTypes.func.isRequired,
  inProgressResetEtaWorkOrder: PropTypes.func.isRequired,
  rescheduleWorkOrder: PropTypes.func.isRequired,
  updateManagerWorkOrder: PropTypes.func.isRequired,
  workOrder: workOrderType,
}
const defaultProps = {}

function OrderShow({
  acceptWorkOrder,
  addTechCommentsWorkOrder,
  clearWorkOrder,
  declineWorkOrder,
  fetchManagerWorkOrder,
  inProgressChangeStatusWorkOrder,
  inProgressResetEtaWorkOrder,
  rescheduleWorkOrder,
  updateManagerWorkOrder,
  workOrder,
}) {
  const { id } = useParams()
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)

  const selectImage = (image) => {
    setIsImageModalOpen(true)
    setSelectedImage(image)
  }

  useEffect(() => {
    fetchManagerWorkOrder(id)
    return () => {
      clearWorkOrder()
    }
  }, [clearWorkOrder, fetchManagerWorkOrder, id])

  const asset = workOrder?.machineAsset
  const assetLines = [
    asset?.make && `Make: ${asset?.make}`,
    asset?.name && `Model: ${asset?.name}`,
    asset?.serialNumber && `Serial Number: ${asset?.serialNumber}`,
  ].filter(Boolean)

  const nte = workOrder?.nte?.toFixed(2) || ''
  const isPoReadOnly = workOrder?.orderStatus === ORDER_STATUS.CLOSED
  const displayValue = (value) =>
    value === 0 || value === false || value ? value : '—'
  const renderManagerField = (label, value, { asHtml = false } = {}) => {
    const shouldRenderHtml = asHtml && looksLikeHtml(value)
    const ValueTag = shouldRenderHtml ? 'div' : 'span'

    return (
      <div className="manager-order-show__detail">
        <span className="manager-order-show__detail-label">{label}</span>
        <ValueTag
          className={
            shouldRenderHtml
              ? 'manager-order-show__detail-value manager-order-show__detail-value--html'
              : 'manager-order-show__detail-value'
          }
        >
          {shouldRenderHtml ? <SafeHtml html={value} /> : displayValue(value)}
        </ValueTag>
      </div>
    )
  }

  if (workOrder?.notFound) {
    return (
      <section className="manager-order-show">
        <div className="manager-order-show__not-found card-styling">
          <h2 className="manager-order-show__not-found-title">
            Order not found
          </h2>
          <Link to="/manager/dashboard">
            <IonButton>Back to Dashboard</IonButton>
          </Link>
        </div>
      </section>
    )
  }

  if (!workOrder) return <LoadingModal />

  return (
    <section className="manager-order-show">
      <Modal
        isOpen={isImageModalOpen}
        dismissModal={() => {
          setIsImageModalOpen(false)
          setSelectedImage(null)
        }}
        header={selectedImage?.filename}
      >
        <img
          alt={selectedImage?.filename}
          src={selectedImage?.url}
          className="work-order-image-full"
        />
      </Modal>
      <div className="manager-order-show__panel-wrapper">
        <div className="manager-order-show__panel card-styling">
          <header>
            <span>Work Order</span>
            <div className="flex">
              <IonText
                color="primary"
                className="body-small mr-auto flex align-center"
              >
                <h2>{workOrder.name}</h2>
              </IonText>
              <StatusTile status={workOrder.orderStatus} />
              <IonButton
                className="manager-order-show__print-button"
                fill="outline"
                color="primary"
                onClick={() => window.print()}
              >
                <IonIcon slot="start" icon={printOutline} aria-hidden="true" />
                Print
              </IonButton>
            </div>
          </header>
          <hr />
          <div className="work-order-preview-content work-order-preview-content--horizontal">
            {workOrder.shouldRunGeolocationCheck && (
              <Link to={`/geolocation_docs`} className="geo-doc-link">
                Problems with Geolocation
                <img alt="Info" src={infoImg} className="geo-doc-info-img" />
              </Link>
            )}
            <div className="work-order-preview-columns m-t-8">
              <div className="work-order-preview-column">
                <h3 className="work-order-preview-column__title">Site</h3>
                <p className="underline work-order-preview-column__location-name">
                  {workOrder.location?.name}
                </p>
                <p className="underline work-order-preview-column__location-name">
                  {workOrder.location?.addressLineOne}
                </p>
                <p className="underline work-order-preview-column__location-name">
                  {workOrder.location?.addressLineTwo}
                </p>
              </div>
              <div className="work-order-preview-column">
                <h3 className="work-order-preview-column__title">
                  Asset Information
                </h3>
                {assetLines.length > 0 ? (
                  assetLines.map((line, i) => <p key={i}>{line}</p>)
                ) : (
                  <p>{workOrder.machineAsset?.name || '—'}</p>
                )}
              </div>
              <div className="work-order-preview-column">
                <h3 className="work-order-preview-column__title">NTE</h3>
                <p>{`$${nte}`}</p>
              </div>
              <div className="work-order-preview-column">
                <h3 className="work-order-preview-column__title">ETA Date</h3>
                <p>{workOrder.etaDate || '—'}</p>
              </div>
              <div className="work-order-preview-column">
                <h3 className="work-order-preview-column__title">
                  Work Completed Date
                </h3>
                <p>
                  {formatUTCAsLocalDate(workOrder.workCompleteDateTime) || '—'}
                </p>
              </div>
            </div>
            <div className="manager-order-show__problem-description">
              <h3 className="work-order-preview-column__title">
                Problem Description
              </h3>
              <p>{workOrder.problemDescription || '—'}</p>
            </div>
            {workOrder.orderStatus === ORDER_STATUS.WORK_COMPLETED && (
              <div className="manager-order-show__problem-description">
                <h3 className="work-order-preview-column__title">
                  Work Performed
                </h3>
                <p>{workOrder.workPerformed || '—'}</p>
              </div>
            )}
          </div>
        </div>

        <SpecialActionsForm
          workOrder={workOrder}
          onAccept={(values) => acceptWorkOrder({ workOrderId: id, ...values })}
          onDecline={(payload) =>
            declineWorkOrder({ workOrderId: id, ...payload })
          }
          onReschedule={(payload) =>
            rescheduleWorkOrder({ workOrderId: id, ...payload })
          }
          onInProgressResetEta={(payload) =>
            inProgressResetEtaWorkOrder({ workOrderId: id, ...payload })
          }
          onInProgressChangeStatus={(payload) =>
            inProgressChangeStatusWorkOrder({ workOrderId: id, ...payload })
          }
          onAddTechComments={(payload) =>
            addTechCommentsWorkOrder({ workOrderId: id, ...payload })
          }
          onRefreshWorkOrder={() => fetchManagerWorkOrder(id)}
        />

        <div className="manager-order-show__panel card-styling">
          <h3 className="manager-order-show__work-order-details-title">
            Work Order Details
          </h3>
          <div className="manager-order-show__work-order-details-sections">
            <div className="manager-order-show__work-order-details-section">
              <h4 className="work-order-preview-column__title">
                Order Instructions
              </h4>
              <div className="manager-order-show__work-order-details-fields">
                {renderManagerField(
                  'Check in Software:',
                  workOrder.ivrPhoneNumber
                )}
                {renderManagerField(
                  'Partner Code:',
                  workOrder.ivrPinPartnerCode
                )}
                {renderManagerField('Order Number:', workOrder.ivrOrderNumber)}
                {renderManagerField(
                  'Order Instructions:',
                  workOrder.ivrInstructions,
                  { asHtml: true }
                )}
              </div>
            </div>
            <div className="manager-order-show__work-order-details-section">
              <h4 className="work-order-preview-column__title">Schedule</h4>
              <div className="manager-order-show__work-order-details-fields">
                {renderManagerField(
                  'Accepted Date & Time:',
                  workOrder.acceptedDateTime &&
                    formatUTCAsLocalDateTime(workOrder.acceptedDateTime)
                )}
                {renderManagerField('ETA Date:', workOrder.etaDate)}
                {renderManagerField('ETA Time:', workOrder.etaTime)}
              </div>
            </div>
            <div className="manager-order-show__work-order-details-section">
              <h4 className="work-order-preview-column__title">Comments</h4>
              <div className="manager-order-show__work-order-details-fields">
                {renderManagerField(
                  'Partner Transaction Log:',
                  workOrder.partnerTransactionLog,
                  { asHtml: true }
                )}
                {renderManagerField('Tech Comments:', workOrder.techComments)}
              </div>
            </div>
            <div className="manager-order-show__work-order-details-section">
              <h4 className="work-order-preview-column__title">Resolution</h4>
              <div className="manager-order-show__work-order-details-fields">
                {renderManagerField(
                  'Work Complete Date Time:',
                  formatUTCAsLocalDateTime(workOrder.workCompleteDateTime)
                )}
                {renderManagerField('Work Performed:', workOrder.workPerformed)}
                {renderManagerField('Root Cause:', workOrder.rootCause)}
                {renderManagerField(
                  'Abuse/Neglect Claims:',
                  workOrder.abuseNeglectClaims
                )}
              </div>
            </div>
            <div className="manager-order-show__work-order-details-section">
              <h4 className="work-order-preview-column__title">Audit</h4>
              <div className="manager-order-show__work-order-details-fields">
                {renderManagerField('Audit Date:', workOrder.auditDate)}
                {renderManagerField('Review Reason:', workOrder.reviewReason)}
                {renderManagerField('Audit Notes:', workOrder.auditNotes)}
              </div>
            </div>
            <div className="manager-order-show__work-order-details-section">
              <h4 className="work-order-preview-column__title">PO Number</h4>
              <div className="manager-order-show__work-order-details-fields">
                <PartsOrderPoNumberForm
                  initialPartsOrderPoNumber={workOrder.partsOrderPoNumber}
                  readOnly={isPoReadOnly}
                  onSave={(partsOrderPoNumber) =>
                    updateManagerWorkOrder({
                      workOrderId: id,
                      partsOrderPoNumber,
                    })
                  }
                />
                {!isPoReadOnly && (
                  <div className="manager-order-show__print-po">
                    {renderManagerField(
                      'Parts Order PO Number:',
                      workOrder.partsOrderPoNumber
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="manager-order-show__panel card-styling">
          <div className="work-order-preview-trips">
            <h3 className="manager-order-show__visits-title">
              Previous Visits
            </h3>
            <IonAccordionGroup className="manager-order-show__visits" multiple>
              {(workOrder.trips || []).map((trip) => (
                <IonAccordion
                  key={trip.id}
                  className="manager-order-show__visit-accordion"
                >
                  <IonItem
                    className="manager-order-show__visit-header"
                    slot="header"
                    lines="none"
                  >
                    <IonLabel>{`Visit on ${trip.date || ''}`}</IonLabel>
                  </IonItem>
                  <div
                    slot="content"
                    className="manager-order-show__visit-content"
                  >
                    <VisitContent
                      workOrder={workOrder}
                      trip={trip}
                      selectImage={selectImage}
                      selectedImage={selectedImage}
                    />
                  </div>
                </IonAccordion>
              ))}
            </IonAccordionGroup>
            {(!workOrder.trips || workOrder.trips.length === 0) && (
              <p className="manager-order-show__no-trips">No previous visits</p>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

OrderShow.propTypes = propTypes
OrderShow.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    workOrder: selectors.workOrder(state),
  }
}

const mapDispatchToProps = {
  acceptWorkOrder: apiActions.acceptWorkOrder,
  addTechCommentsWorkOrder: apiActions.addTechCommentsWorkOrder,
  declineWorkOrder: apiActions.declineWorkOrder,
  rescheduleWorkOrder: apiActions.rescheduleWorkOrder,
  inProgressResetEtaWorkOrder: apiActions.inProgressResetEtaWorkOrder,
  inProgressChangeStatusWorkOrder: apiActions.inProgressChangeStatusWorkOrder,
  updateManagerWorkOrder: apiActions.updateManagerWorkOrder,
  fetchManagerWorkOrder: apiActions.fetchManagerWorkOrder,
  clearWorkOrder: actions.clearWorkOrder,
}

export default connect(mapStateToProps, mapDispatchToProps)(OrderShow)
